import { z } from 'zod';
export const JiraSubtaskDTO = z.object({
    key: z.string(),
    summary: z.string(),
    status: z.string(),
    url: z.string(),
    // Statut regroupé par catégorie Jira (statusCategory.key === 'done'), pas par comparaison de
    // libellé — un workflow localisé ("Terminé") ou personnalisé ne s'appelle pas forcément "Done"
    // littéralement, voir isDoneStatus (services/jira.ts). Pilote la couleur verte de la pastille
    // dans la liste dépliée (RepoJiraTickets.tsx).
    isDone: z.boolean(),
});
// Version Jira (champ "Fix versions") — `id` est ce qu'attend l'écriture (editIssue/fixVersions),
// `name` ce qu'affiche l'UI.
export const JiraVersionDTO = z.object({
    id: z.string(),
    name: z.string(),
});
export const JiraTicketDTO = z.object({
    key: z.string(),
    summary: z.string(),
    status: z.string(),
    assignee: z
        .object({
        accountId: z.string(),
        displayName: z.string(),
        avatarUrl: z.string().nullable(),
    })
        .nullable(),
    url: z.string(),
    // Droit ASSIGN_ISSUES du compte Jira de l'utilisateur sur le projet du ticket (voir
    // getMyPermissions dans services/jira.ts) — permet de désactiver le bouton d'édition d'assignee
    // plutôt que de laisser l'utilisateur découvrir un 403 après coup. Fail-open (true) si la
    // vérification elle-même échoue : ne pas bloquer une capacité déjà existante à cause d'un
    // problème sans rapport (rate limit, etc.) — seul un `havePermission: false` explicite masque le
    // bouton.
    canAssign: z.boolean(),
    // Champ Jira "Fix versions" du ticket (peut contenir des versions déjà publiées : on affiche
    // l'état réel, seule la liste proposée à l'édition est filtrée sur `released: false`).
    fixVersions: z.array(JiraVersionDTO),
    // Droit EDIT_ISSUES sur le projet du ticket, analogue à canAssign (mêmes règles fail-open, voir
    // canEditIssues dans services/jira.ts). [Supposition] EDIT_ISSUES suffit à modifier fixVersions
    // — à confirmer sur l'instance réelle (un écran d'édition Jira peut en restreindre les champs).
    canEditFixVersions: z.boolean(),
    // Sous-tâches Jira natives du ticket — pour le dépliage au clic dans la sidebar "Tickets Jira"
    // (RepoJiraTickets.tsx). Tableau vide si le ticket n'en a pas (pas de champ optionnel : la liste
    // n'a pas besoin de distinguer "aucune subtask" de "pas encore chargé", cette DTO n'existe de
    // toute façon qu'une fois le detail chargé).
    subtasks: z.array(JiraSubtaskDTO),
    // Même booléen catégorisé (statusCategory.key === 'done', voir isDoneStatus côté service) que
    // RepoTicketGroup.isDone/JiraSubtaskDTO.isDone — pilote la couleur verte des pastilles de ticket
    // (sidebar ET pastilles "Refs:" de la tooltip de commit, voir GitGraph.tsx).
    isDone: z.boolean(),
});
export const BranchJiraResolution = z.object({
    branchName: z.string(),
    ticketKey: z.string().nullable(),
    source: z.enum(['branch-name', 'jira-field', 'manual', 'none']),
    ticket: JiraTicketDTO.nullable(),
});
export const ManualBranchLinkInput = z.object({
    repoId: z.string().min(1),
    branchName: z.string().min(1),
    ticketKey: z.string().regex(/^[A-Z][A-Z0-9_]+-\d+$/, 'Format attendu : PROJ-123'),
});
export const UpdateAssigneeInput = z.object({
    ticketKey: z.string().regex(/^[A-Z][A-Z0-9_]+-\d+$/),
    accountId: z.string().min(1).nullable(),
});
export const UpdateFixVersionsInput = z.object({
    ticketKey: z.string().regex(/^[A-Z][A-Z0-9_]+-\d+$/),
    versionIds: z.array(z.string().min(1)),
});
// Résultat de userSearch.findAssignableUsers (services/jira.ts) — pour l'autocomplétion d'assignee
// dans JiraTicketTooltip.tsx. avatarUrl nullable comme sur JiraTicketDTO.assignee (même raison :
// dépend du réglage de confidentialité du compte Atlassian).
export const JiraAssignableUserDTO = z.object({
    accountId: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
});
// Résultat de issueSearch.getIssuePickerResource (services/jira.ts) — le picker "issue picker"
// natif de Jira (celui derrière son propre dialogue "Link issue"), pour le picker de liaison
// manuelle branche↔ticket dans BranchContextMenu.tsx. Comme les suggestions viennent directement
// de Jira, `key` est garanti exister et accessible à l'utilisateur — pas de validation séparée à
// faire avant l'upsert dans /jira/branch-link.
export const JiraIssueSuggestionDTO = z.object({
    key: z.string(),
    summary: z.string(),
});
// Transition de workflow Jira disponible pour CE ticket et CET utilisateur (issues.getTransitions
// ne renvoie que celles réellement exécutables — pas de champ `canTransition` séparé à vérifier
// côté DTO, une liste vide suffit à signaler l'absence de droit ou l'absence de transition
// possible depuis le statut courant). `name` est le libellé de la transition elle-même (ex.
// "Terminer"), pas le nom du statut cible — c'est ce qu'affiche le menu déroulant.
export const JiraTransitionDTO = z.object({
    id: z.string(),
    name: z.string(),
});
export const TransitionTicketInput = z.object({
    ticketKey: z.string().regex(/^[A-Z][A-Z0-9_]+-\d+$/),
    transitionId: z.string().min(1),
});
// Sidebar "Tickets Jira" (RepoViewPage.tsx) — un ticket peut regrouper plusieurs branches (voir
// la conversation : `feat/GU-332` et `feat/GU-332-2` résolvent toutes deux vers `GU-332`).
// Le regroupement lui-même (ticketKey/branches) ne nécessite aucun appel Jira (juste extraction +
// liens manuels, voir resolveRepoTickets côté service) ; `url` est déjà résolue côté serveur
// (resolveUserJiraBaseUrl) pour ne pas dupliquer cette logique côté client, nulle si l'utilisateur
// n'a ni URL personnelle ni défaut serveur. `summary` en revanche coûte un appel Jira par ticket
// unique (resolveRepoTicketSummaries) — nulle si l'appel échoue (ticket supprimé, inaccessible…)
// plutôt que de faire échouer tout le groupe pour un seul ticket en erreur. `hasSubtasks` et
// `isDone` viennent du même appel (champs `subtasks`/`status` demandés en plus de `summary`, coût
// marginal) — justes des booléens pour l'affichage dans la liste (indicateur de subtasks, couleur
// de la pastille) ; le détail des subtasks (clé/titre/statut) n'est chargé qu'au clic sur la
// ligne, voir JiraTicketDTO.subtasks et getTicket côté service.
export const RepoTicketGroup = z.object({
    ticketKey: z.string(),
    branches: z.array(z.string()),
    url: z.string().nullable(),
    summary: z.string().nullable(),
    hasSubtasks: z.boolean(),
    isDone: z.boolean(),
    // Au moins une fix version posée — booléen pour la pastille de la liste (RepoJiraTickets.tsx),
    // même appel que `summary`/`isDone` (champ `fixVersions` ajouté à la requête, coût marginal).
    hasFixVersions: z.boolean(),
});
export const RepoTicketsResponse = z.array(RepoTicketGroup);
// Gate d'affichage de la sidebar "Tickets Jira" (RepoViewPage.tsx) — distinct de hasJira
// (SessionDTO, qui ne vérifie que la présence du token) : canBrowse appelle réellement Jira
// (getMyPermissions sur BROWSE_PROJECTS) pour s'assurer que le token, s'il existe, donne au moins
// de quoi consulter des tickets. hasToken séparé pour distinguer les deux messages ("configure un
// token" vs "droits insuffisants") côté UI.
export const JiraAccessDTO = z.object({
    hasToken: z.boolean(),
    canBrowse: z.boolean(),
    // URL de base déjà résolue côté serveur (resolveUserJiraBaseUrl) — exposée ici pour que le
    // client puisse construire lui-même un lien /browse/<clé> vers une clé de ticket arbitraire
    // (ex: référence "Refs: GU-XX" dans un message de commit, jamais résolue par ailleurs) sans
    // dépendre d'un DTO par-ticket déjà construit côté serveur. null si aucune URL n'est configurée
    // (ni préférence utilisateur, ni défaut serveur) — même sémantique que RepoTicketGroup.url.
    jiraBaseUrl: z.string().nullable(),
});
//# sourceMappingURL=jira.js.map